import type { MetaFunction } from "@remix-run/node";
import { Accordion, AccordionItem } from "@heroui/react";
import { FaQuestion } from "react-icons/fa";
import { Link, useLoaderData } from "@remix-run/react";
import { fetchFaqApi } from "app/api/faq.api";
import { faqSchema } from "./seo";
import { SEOHandle } from "@nasa-gcn/remix-seo";
import { getSocialMetaTags } from "app/utils/meta";

interface FAQ {
  title: string;
  answer: string;
  section: { _id: string; title: string };
  _id: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

interface GroupedFAQs {
  [key: string]: FAQ[];
}

export function headers() {
  return {
    "cache-control": "max-age=604800, stale-while-revalidate=86400",
  };
}

export const meta: MetaFunction = () =>
  getSocialMetaTags({
    title: "FAQ | Promotional Products Now",
    description:
      "Find answers to common questions about Promotional Products Now, including our services and products.",
    url: "https://www.promotionalproductsnow.au/faq",
    extra: [
      { tagName: "link", rel: "canonical", href: "https://www.promotionalproductsnow.au/faq" },
      { name: "robots", content: "index, follow" },
    ],
  });

function groupFAQsBySection(faqs: FAQ[]): GroupedFAQs {
  return faqs.reduce((acc: GroupedFAQs, faq: FAQ) => {
    const sectionTitle = faq?.section?.title ?? "Uncategorized";
    if (!acc[sectionTitle]) {
      acc[sectionTitle] = [];
    }
    acc[sectionTitle].push(faq);
    return acc;
  }, {});
}

export const loader = async () => {
  try {
    const { data } = await fetchFaqApi();
    const groupedFAQs = groupFAQsBySection(data?.payload?.data || []);
    return groupedFAQs;
  } catch (error) {
    console.error("Failed to fetch FAQs:", error);
    return {};
  }
};

const Faq = () => {
  const groupedFAQs = useLoaderData<GroupedFAQs>();

  return (
    <div lang="en" className="flex flex-col gap-3 w-full mx-auto space-y-6">
      <div className="flex flex-col gap-3 text-center pt-16 md:pt-0">
        <h2
          itemProp="headline"
          className="text-2xl md:text-3xl xl:text-4xl 2xl:text-5xl 4xl:text-6xl font-bold"
        >
          FAQs
        </h2>
        <p className="text-default-500 dark:text-zinc-100  text-sm md:text-base">
          Here are the most frequently asked questions. We are here to help you, so please feel free
          to{" "}
          <Link
            itemProp="url"
            to="/contact"
            prefetch="intent"
            className="text-darkerPrimary  dark:text-yellow font-semibold cursor-pointer"
            title="Contact us for further inquiries"
          >
            Contact us
          </Link>
          .
        </p>
      </div>

      <div className="flex flex-col gap-2 py-4 md:p-0">
        {Object.keys(groupedFAQs).map((section) => (
          <div key={section}>
            <h2 className="text-left text-darkerPrimary  dark:text-yellow ml-2">FAQ - {section}</h2>
            {groupedFAQs[section].map((faq) => (
              <Accordion variant="light" key={faq._id} showDivider={false}>
                <AccordionItem
                  key={faq._id}
                  aria-label={`Accordion-${faq._id}`}
                  title={faq.title}
                  className="border border-neutral-200 dark:border-neutral-700 my-2 px-3"
                  classNames={{ title: "font-semibold text-sm md:text-lg" }}
                >
                  <div className="border-t border-neutral-200 dark:border-neutral-700  mb-2"></div>
                  <div className="w-full px-3">
                    <p className="text-foreground-600 text-sm leading-loose">{faq.answer}</p>
                  </div>
                </AccordionItem>
              </Accordion>
            ))}
          </div>
        ))}
      </div>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema(groupedFAQs)) }}
      />
    </div>
  );
};

export const handle: SEOHandle = {
  getSitemapEntries: async () => [
    {
      route: "/faq",
      priority: 0.6,
      lastmod: new Date().toISOString(),
      changefreq: "weekly",
    },
  ],
};

export default Faq;
